import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
} from "@dnd-kit/modifiers";
import SwitchOperation from "./SwitchOperation";
import AddOperation from "./lib/AddOperation";
import OperationContext from "./lib/OperationContext";
import { components } from "@/lib/ApiSchema";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import useUpdateOperation, {
  useMoveOperation,
} from "@/lib/queries/useUpdateOperation";
import dayjs from "dayjs";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import SubpartList from "./SubpartList";
import CollapseIcon from "~icons/fa6-solid/caret-down";
import { getFractionalIndex } from "@/lib/utils";

export default function OperationList({
  title,
  operations,
  quantities,
  generalCalculatorType,
  canEditPart,
  subParts,
}: {
  title: string;
  generalCalculatorType: components["schemas"]["CalculatorGeneralTypeEnum"];
  operations: components["schemas"]["RequestedPartWithOperationsSchema"]["operations"];
  quantities: number[];
  canEditPart: boolean;
  subParts: components["schemas"]["RequestedPartWithOperationsSchema"]["children"];
}) {
  const { teamSlug } = useParams();
  const { mutate: moveOperation } = useMoveOperation();
  const updateOperation = useUpdateOperation();
  const [activeId, setActiveId] = useState<string | null>(null);

  const canEdit = !!useGetCurrentQuoteRevision()?.can_edit && canEditPart;
  const [expanded, setExpanded] = useState(true);

  const [items, setItems] = useState(() => operations);
  useEffect(() => {
    setItems(operations);
  }, [operations]);

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
    >
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <button
            className="cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <CollapseIcon
              className={classNames(
                "size-5 text-gray-400 hover:text-gray-600 transition-transform",
                {
                  "rotate-270": !expanded,
                  "rotate-360": expanded,
                },
              )}
            />
          </button>
          <h3 className="font-bold text-base">{title}</h3>
        </div>
        {canEdit && (
          <AddOperation
            first
            quantities={quantities}
            generalCalculatorType={generalCalculatorType}
            ordinal={getFractionalIndex(
              items[items.length - 1]?.ordinal ?? null,
              null,
            )}
          />
        )}
        <div className="border-b-2 flex-1" />
      </div>
      {expanded &&
        generalCalculatorType === "material" &&
        subParts.length > 0 && (
          <SubpartList subparts={subParts} canEdit={canEdit} />
        )}
      {expanded && (
        <div
          className={classNames("flex flex-col py-1", {
            "gap-2": !canEdit,
          })}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((data, i) => (
              <div key={data.id} className="flex flex-col">
                {canEdit && (
                  <AddOperation
                    key={data.id + i}
                    quantities={quantities}
                    generalCalculatorType={generalCalculatorType}
                    ordinal={getFractionalIndex(
                      items[i - 1]?.ordinal ?? null,
                      items[i]?.ordinal ?? null,
                    )}
                  />
                )}
                <OperationContext.Provider
                  value={{
                    operationData: data,
                    allowActions: true,
                    updateOperation,
                  }}
                  key={data.id}
                >
                  <div
                    className={classNames("flex", {
                      "pointer-events-none": !canEdit,
                      "animate-pulse":
                        Math.abs(dayjs(data.created_at).diff()) < 5000,
                    })}
                    style={{ animationIterationCount: 1 }}
                  >
                    <SwitchOperation type={data.type} />
                  </div>
                </OperationContext.Provider>
                {canEdit && i === items.length - 1 && (
                  <AddOperation
                    key={data.id + i + 1}
                    quantities={quantities}
                    generalCalculatorType={generalCalculatorType}
                    ordinal={getFractionalIndex(
                      items[items.length - 1]?.ordinal ?? null,
                      null,
                    )}
                  />
                )}
              </div>
            ))}
          </SortableContext>

          <DragOverlay>
            {activeId ? (
              <OperationContext.Provider
                value={{
                  operationData: items.find((op) => op.id === activeId)!,
                  allowActions: true,
                  updateOperation,
                }}
              >
                <div className="flex">
                  <SwitchOperation
                    type={items.find((op) => op.id === activeId)!.type}
                  />
                </div>
              </OperationContext.Provider>
            ) : null}
          </DragOverlay>
        </div>
      )}
    </DndContext>
  );

  function handleDragStart(event: DragEndEvent) {
    setActiveId(event.active.id.toString());
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (items && active.id !== over?.id) {
      const oldIndex = active.data.current?.sortable.index;
      const newIndex = over?.data.current?.sortable.index;
      const reorderedItems = arrayMove(items, oldIndex, newIndex);

      const leftOrdinal =
        newIndex === 0 ? null : reorderedItems[newIndex - 1].ordinal;

      const rightOrdinal =
        newIndex === reorderedItems.length - 1
          ? null
          : reorderedItems[newIndex + 1].ordinal;

      const newOrdinal = getFractionalIndex(leftOrdinal, rightOrdinal);

      // optimistic client update
      reorderedItems[newIndex].ordinal = newOrdinal;
      setItems(reorderedItems);

      // server update
      moveOperation({
        params: { path: { id: active.id.toString(), team_slug: teamSlug! } },
        body: { ordinal: newOrdinal },
      });
    }
    setActiveId(null);
  }
}
